import type { FC } from 'react';
import React, { useState } from 'react';
import type { MenuMode } from 'rc-menu/lib/interface';
import Link from 'next/link';
import { Menu } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  CommentOutlined,
  InboxOutlined,
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import { useRouter } from 'next/router';

import { useAuth } from 'src/util/use-auth';

import { SwitchProfileModal } from './Avatar/SwitchProfileModal';

interface Props {
  mode?: MenuMode;
  className: string;
  onClick?: () => void;
}

export const MenuContent: FC<Props> = ({ mode, className, onClick }) => {
  const { t } = useTranslation('menu');
  const { user } = useAuth();
  const router = useRouter();
  const [switchModalVisible, setSwitchModalVisible] = useState(false);

  const handleSwitchProfileClick = () => {
    setSwitchModalVisible(true);
    if (onClick) onClick(); // Close mobile menu
  };

  const handleLogoutClick = () => {
    router.push('/logout');
    if (onClick) onClick(); // Close mobile menu
  };

  const displayName = user
    ? `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
      user.username ||
      user.email
    : '';

  return (
    <>
      <Menu
        className={className}
        mode={mode}
        style={{ width: '100%', background: 'none' }}
      >
        {/* User section for mobile */}
        {mode === 'inline' && user && (
          <>
            <Menu.SubMenu
              key="user-menu"
              icon={<UserOutlined />}
              title={displayName}
            >
              <Menu.Item
                key="settings"
                icon={<SettingOutlined />}
                onClick={onClick}
              >
                <Link href="/account">{t('Settings')}</Link>
              </Menu.Item>
              <Menu.Item key="inbox" icon={<InboxOutlined />} onClick={onClick}>
                <Link href="/inbox">{t('Inbox')}</Link>
              </Menu.Item>
              <Menu.Item
                key="support-requests"
                icon={<CommentOutlined />}
                onClick={onClick}
              >
                <Link href="/feedback/support_requests">
                  {t('Support Requests')}
                </Link>
              </Menu.Item>
              {user?.multiple_profiles && (
                <Menu.Item
                  key="switch-profile"
                  icon={<SwapOutlined />}
                  onClick={handleSwitchProfileClick}
                >
                  {t('Switch Profile')}
                </Menu.Item>
              )}
              <Menu.Divider />
              <Menu.Item
                key="logout"
                icon={<LogoutOutlined />}
                danger
                onClick={handleLogoutClick}
              >
                {t('Logout')}
              </Menu.Item>
            </Menu.SubMenu>
            <Menu.Divider />
          </>
        )}

        <Menu.Item
          key="dashboard"
          onClick={onClick}
          data-cy="dashboard-nav-bar"
        >
          <Link href="/dashboard">{t('Dashboard')}</Link>
        </Menu.Item>
        <Menu.Item
          key="articles"
          onClick={onClick}
          data-cy="menu-item-articles"
        >
          <Link href="/articles">{t('Articles')}</Link>
        </Menu.Item>
        <Menu.Item key="companies" onClick={onClick}>
          <Link href="/companies">{t('Companies')}</Link>
        </Menu.Item>
        <Menu.Item key="alerts" onClick={onClick}>
          <Link href="/alerts">{t('Alerts')}</Link>
        </Menu.Item>
        <Menu.Item key="binders" onClick={onClick}>
          <Link href="/binders">{t('Binders')}</Link>
        </Menu.Item>
        <Menu.Item key="portfolios" onClick={onClick}>
          <Link href="/portfolios">{t('Portfolios')}</Link>
        </Menu.Item>
        <Menu.Item key="compliance" onClick={onClick}>
          <Link href="/compliance">{t('Compliance')}</Link>
        </Menu.Item>
        <Menu.Item key="reports" onClick={onClick}>
          <Link href="/reports">{t('Reports')}</Link>
        </Menu.Item>
        {/*<Menu.Item key="stats" onClick={onClick}>*/}
        {/*  <Link href="/stats">{t('Analytics')}</Link>*/}
        {/*</Menu.Item>*/}
      </Menu>

      {/* Switch Profile Modal */}
      <SwitchProfileModal
        visible={switchModalVisible}
        onClose={() => setSwitchModalVisible(false)}
      />
    </>
  );
};
